import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PRODUCTS } from '../data/products';
import { INDUSTRIES } from '../data/industries';

const SITE_URL = 'https://loanyardcapital.com';
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const CANONICAL_MAP = {  '/partner': '/become-a-partner',
  '/become-partner': '/become-a-partner',
  '/home-loans': '/home-loan',
  '/homesaver-home-loans': '/home-loan',
  '/working-capital-loan': '/working-capital-finance',
  '/cgtsme-loan': '/cgtmse-loan',
  '/unsecured-business-loan': '/business-loans',
  '/commercial-property-purchase-loan': '/commercial-property-purchase',
  '/balance-transfer-with-topup': '/',
  '/calculator/home-loan-emi': '/calculators/emi',
  '/calculator/personal-loan-emi': '/calculators/emi',
  '/calculator/car-loan-emi': '/calculators/emi',
  '/calculator/education-loan-emi': '/calculators/emi',
  '/calculator/home-loan-eligibility': '/calculators/eligibility'
};

const META = {
  '/': {
    title: 'LOANYARD CAPITAL',
    description: 'LOANYARD CAPITAL - Professional loan advisory in India. Home, business & property loans from top financial institutions with quick approval and best rates.'
  },
  '/become-a-partner': {
    title: 'Become a Partner | LOANYARD CAPITAL',
    description: 'Partner with LOANYARD CAPITAL and earn attractive payouts by referring home, business and property loan customers across India.'
  },
  '/cibil-score': {
    title: 'Check CIBIL Score | LOANYARD CAPITAL',
    description: 'Check your CIBIL score and learn how to improve it for faster loan approvals and better interest rates with LOANYARD CAPITAL.'
  },
  '/calculators/emi': {
    title: 'EMI Calculator | LOANYARD CAPITAL',
    description: 'Free EMI calculator for home, personal and business loans. Instantly calculate monthly installments, total interest and repayment.'
  },
  '/calculators/eligibility': {
    title: 'Loan Eligibility Calculator | LOANYARD CAPITAL',
    description: 'Check how much loan you are eligible for based on your income and obligations. Free eligibility calculator by LOANYARD CAPITAL.'
  },
  '/calculators/overdraft': {
    title: 'Overdraft Calculator | LOANYARD CAPITAL',
    description: 'Calculate interest costs on your overdraft facility - daily, monthly and yearly interest on utilised amounts.'
  },
  '/calculators/home-loan-overdraft': {
    title: 'Home Loan Overdraft Calculator | LOANYARD CAPITAL',
    description: 'See how much interest you save by parking surplus funds in a home loan overdraft account. Free calculator.'
  },
  '/calculators/balance-transfer': {
    title: 'Home Loan Balance Transfer Calculator | LOANYARD CAPITAL',
    description: 'Calculate savings from transferring your home loan to a lower rate - new EMI, monthly savings and net benefit after fees.'
  },
  '/calculators/buy-vs-rent': {
    title: 'Buy Vs Rent Calculator | LOANYARD CAPITAL',
    description: 'Should you buy a home or keep renting? Compare total costs, property appreciation and net wealth over the long term.'
  },
  '/calculators/prepayment': {
    title: 'Loan Prepayment Calculator | LOANYARD CAPITAL',
    description: 'See how part-prepayments reduce total interest and shorten your loan tenure. Free prepayment calculator by LOANYARD CAPITAL.'
  },
  '/about-us': {
    title: 'About Us — Corporate Finance & Capital Advisory | LOANYARD CAPITAL',
    description: 'LOANYARD CAPITAL is a corporate finance and capital advisory firm working across debt, structured credit, asset-backed finance and strategic capital requirements.'
  },
  '/calculators': {
    title: 'Financial Calculators — EMI, Eligibility, Overdraft & More | LOANYARD CAPITAL',
    description: 'Free financial calculators by LOANYARD CAPITAL — EMI, loan eligibility, overdraft, balance transfer, prepayment and buy vs rent.'
  },
  '/insights': {
    title: 'Capital Insights — Corporate Finance & Debt Market Perspectives | LOANYARD CAPITAL',
    description: 'Perspectives on corporate finance, debt markets, working capital, real estate finance and India\u2019s evolving lending environment.'
  },
  '/insights/working-capital-cash-credit-vs-od-vs-wcdl': {
    title: 'Working Capital Finance: Cash Credit vs Overdraft vs WCDL | LOANYARD CAPITAL',
    description: 'How should a business choose between cash credit, overdraft and working capital demand loans? A structuring-led comparison for CFOs and promoters.'
  },
  '/insights/debt-syndication-mid-market-india': {
    title: 'Debt Syndication in India for Mid-Market Companies | LOANYARD CAPITAL',
    description: 'How mid-market companies raise structured debt across multiple banks, NBFCs and institutions — process, benefits and where advisory adds value.'
  },
  '/insights/lrd-vs-lap-real-estate-capital': {
    title: 'Lease Rental Discounting vs Loan Against Property | LOANYARD CAPITAL',
    description: 'LRD prices against contracted rentals while LAP prices against asset value. How to structure real estate-backed capital for the right tenor and pricing.'
  },
  '/industries': {
    title: 'Industries We Serve | LOANYARD CAPITAL',
    description: 'Sector-specific funding expertise across manufacturing, healthcare, real estate, IT, trade and education — with real case studies from 30+ industries.'
  }
};

const setMeta = (selector, attr, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [key, val] = selector.match(/\[(.+?)="(.+?)"\]/).slice(1, 3);
    el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

export const resolveMeta = (pathname) => {
  const cleanPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
  const canonicalPath = CANONICAL_MAP[cleanPath] || cleanPath;
  const href = canonicalPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${canonicalPath}`;
  const meta = META[canonicalPath]
    || (canonicalPath.length > 1 && PRODUCTS[canonicalPath.slice(1)] && {
      title: PRODUCTS[canonicalPath.slice(1)].metaTitle,
      description: PRODUCTS[canonicalPath.slice(1)].metaDescription
    })
    || (canonicalPath.startsWith('/industries/') && INDUSTRIES[canonicalPath.slice(12)] && {
      title: INDUSTRIES[canonicalPath.slice(12)].metaTitle,
      description: INDUSTRIES[canonicalPath.slice(12)].metaDescription
    })
    || META['/'];
  return { cleanPath, canonicalPath, href, title: meta.title, description: meta.description };
};

export { SITE_URL, OG_IMAGE, META, CANONICAL_MAP };

export const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const { cleanPath, href, title, description } = resolveMeta(pathname);

    document.title = title;

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);

    setMeta('meta[name="robots"]', 'content', cleanPath === '/my-leads' ? 'noindex, nofollow' : 'index, follow');
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', href);
    setMeta('meta[property="og:image"]', 'content', OG_IMAGE);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', OG_IMAGE);
  }, [pathname]);

  return null;
};
